import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { Button, DatePicker, Select, Input, Spin, message } from "antd";
import dayjs from "dayjs";
import { ArrowsLeftRight, Plus, X } from "@phosphor-icons/react";
import { getWorkItemContext, createWorkItem } from "../../service/inbox";
import {
  getMatterWorkItemContext,
  createMatterWorkItem,
} from "../../service/workItems";
import {
  InboxModalErrorBanner,
  InboxModalFieldGroup,
  InboxModalHint,
  InboxModalLabel,
} from "../AIPane/AIPaneStyled";
import BesideDocumentDrawer from "./besideDocumentDrawer";

/**
 * Create Work Item — the NEW Salesforce org's attorney→paralegal baton.
 * Two anchors, one form:
 *
 *   - `documentId` — composed from the inbox document that prompted it;
 *     a deep link back to the document is appended server-side.
 *   - `matterId`   — composed from the matter page (8/31, "Create work
 *     item and then I get the options"); no document involved.
 *
 * Different animal from Create Task (Litify) and Send message (DMS):
 * a work item is accountable work with a finish line. It lands in the
 * assignee's court in the new org's Work Items panel, they return it
 * with what they did, and the requester closes the loop.
 *
 * On open it asks the backend whether the matter has migrated to the
 * new org. Three honest states:
 *   - not enabled in this environment → say so
 *   - matter not migrated yet         → say so, point at Create Task
 *   - ready                           → recipients are the NEW org's
 *     matter team (case manager preselected), NOT the DMS user list —
 *     the same picker the claim tracker's composer shows.
 *
 * Opens as a right-hand panel so an open document stays readable.
 * Switching letters while it is open keeps the draft; Send links the
 * letter currently on screen.
 *
 * **How it is built (Matthew, 10/8).** One composer, three buttons:
 *
 *   - The Instruct chips pick what kind of work *this step* is; the
 *     Instruction box holds its words. Conditions and Due belong to the
 *     work item being written.
 *   - **Add a step** turns the work item into a plan: the step so far
 *     joins the list above the composer and the next one is written in
 *     the same box. A plan goes out as one ordered chain — step 1 live,
 *     each next step greyed "Coming up" until the one before it is
 *     closed.
 *   - **Add work item** puts the work item (single or plan) on the
 *     "Ready to send" list and clears the composer for the next one.
 *     Items on that list are separate — nothing waits on anything.
 *   - **Send** sends the list plus whatever is still in the composer, in
 *     one go with one bell. So one work item is written and sent with
 *     Send alone; nobody has to add before sending a single one.
 *
 * Several work items leave as `items: [...]`; a single one keeps the
 * original flat shape (or `chain` for a plan) so an older backend still
 * accepts the common case.
 *
 * **Due date.** Optional, and the only thing that makes an item read as
 * due or overdue: a date typed into the instruction is just words
 * (Matthew, 9/21). One date covers a plan — every step takes it.
 */

/**
 * Subject line for a set of pinned conditions — same rule the org
 * applies when the subject is blank (one name; up to three, comma
 * list; more, "7 conditions: A, B, C +4 more"). Preview only; the org
 * writes the real one.
 */
export const conditionLabel = (names) => {
  if (!names || names.length === 0) return "";
  if (names.length <= 3) return names.join(", ");
  return `${names.length} conditions: ${names.slice(0, 3).join(", ")} +${
    names.length - 3
  } more`;
};

/**
 * Fallback type list — used only when the context response has no
 * `types` field (an older doorway, before the org started sending its
 * own `{value, label}` composer list). The doorway is the source of
 * truth once it answers with `types`.
 */
export const WORK_ITEM_TYPES = Object.freeze([
  "Get Statement",
  "Order Exam",
  "Request Records",
  "Draft Appeal",
  "Call Client",
  "File Review",
  "Decision Review",
  "Document Review",
  "Other",
]);

const escapeHtml = (text) =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/** Plain textarea input → simple HTML paragraphs for Instruction__c. */
export const toInstructionHtml = (text) =>
  String(text)
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join("");

/**
 * One finished work item → the doorway's body keys. A single step is
 * the flat shape; two or more are an ordered `chain` (a plan). Pins
 * and due date ride along either way.
 */
export const workItemBody = (item) => {
  const body = {};
  if (item.steps.length === 1) {
    body.type = item.steps[0].type;
    // Blank subject lets the org compose "Order exam — Migraines" from
    // the type label and the pins; Other keeps the user's own words,
    // having no label worth reading.
    body.subject = item.subject || "";
    body.instruction = toInstructionHtml(item.steps[0].instruction);
  } else {
    body.chain = item.steps.map((step) => ({
      type: step.type,
      instruction: toInstructionHtml(step.instruction),
    }));
    body.linked = true;
  }
  if (item.conditionIds && item.conditionIds.length > 0) {
    body.condition_ids = item.conditionIds;
  }
  if (item.dueDate) {
    body.due_date = item.dueDate.format("YYYY-MM-DD");
  }
  return body;
};

/* ---- small, local styles -------------------------------------------- */

const chipRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  alignItems: "center",
};

const chipStyle = {
  display: "inline-flex",
  alignItems: "center",
  padding: "2px 10px",
  borderRadius: 999,
  border: "1px solid #d9d9d9",
  background: "#fff",
  fontSize: 12,
  lineHeight: "18px",
  cursor: "pointer",
  userSelect: "none",
  font: "inherit",
};

const chipOnStyle = {
  ...chipStyle,
  border: "1px solid #722ed1",
  background: "#722ed1",
  color: "#fff",
};

const composerStyle = {
  marginTop: 4,
  padding: "10px 12px",
  border: "1px solid #e8e8e8",
  borderRadius: 8,
  background: "#fafafa",
};

const composerHeadStyle = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: 8,
  marginBottom: 8,
  fontSize: 13,
  fontWeight: 600,
};

const planStripStyle = {
  marginBottom: 10,
  padding: "6px 10px",
  borderLeft: "3px solid #722ed1",
  background: "#f9f0ff",
  fontSize: 12,
  lineHeight: 1.5,
};

const stepPillStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "1px 8px",
  margin: "2px 2px 2px 0",
  borderRadius: 999,
  border: "1px solid #d3adf7",
  background: "#fff",
  fontSize: 12,
};

const tinyXStyle = {
  display: "inline-flex",
  alignItems: "center",
  border: "none",
  background: "none",
  padding: 0,
  cursor: "pointer",
  color: "#8c8c8c",
  lineHeight: 1,
};

const actionsRowStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 8,
  marginTop: 10,
};

const actionHintStyle = {
  margin: "4px 0 0",
  fontSize: 11,
  lineHeight: 1.35,
  color: "#595959",
};

const readyListStyle = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  border: "1px solid #b7eb8f",
  borderRadius: 8,
  background: "#f6ffed",
};

const readyRowStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 8,
  padding: "6px 10px",
  fontSize: 12,
  lineHeight: 1.45,
  borderTop: "1px solid #d9f7be",
};

const warnStyle = { color: "#a8071a", marginTop: 6, fontSize: 12 };

const hasText = (value) => String(value || "").trim().length > 0;

let nextQueuedId = 1;

const CreateWorkItemModal = ({ open, documentId, matterId, onClose, onCreated }) => {
  const [contextStatus, setContextStatus] = useState("idle");
  const [context, setContext] = useState(null);
  const [contextFor, setContextFor] = useState(null);
  // The whole send goes to one person.
  const [assigneeId, setAssigneeId] = useState(null);
  // The step being written right now.
  const [type, setType] = useState(null);
  const [subject, setSubject] = useState("");
  const [instruction, setInstruction] = useState("");
  // The work item being written: steps already added (a plan in the
  // making), plus its pins and due date.
  const [steps, setSteps] = useState([]);
  const [conditionIds, setConditionIds] = useState([]);
  const [dueDate, setDueDate] = useState(null);
  // Work items finished with "Add work item", waiting for Send.
  const [queued, setQueued] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const textAreaRef = useRef(null);

  // The document anchor wins when both are given — it carries the deep
  // link back to the evidence that prompted the instruction.
  const documentAnchored = documentId != null;
  const currentContextKey = documentAnchored
    ? `d:${documentId}`
    : `m:${matterId}`;
  const contextIsCurrent = contextFor === currentContextKey;

  const resetComposer = useCallback(() => {
    setType(null);
    setSubject("");
    setInstruction("");
    setSteps([]);
    setConditionIds([]);
    setDueDate(null);
  }, []);

  // Wipe the draft only when the panel is closed. Switching the open
  // letter must not throw away what they were writing — attorneys bounce
  // through mail while composing (Matthew, 2026-09-14).
  useEffect(() => {
    if (open) return;
    setAssigneeId(null);
    resetComposer();
    setQueued([]);
    setSubmitError(null);
    setContext(null);
    setContextStatus("idle");
    setContextFor(null);
  }, [open, resetComposer]);

  // Fresh matter team for whichever letter is on screen. If we already
  // showed the form, keep it up while this loads so the panel stays
  // still; only the first open shows the spinner.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const loadingKey = documentAnchored ? `d:${documentId}` : `m:${matterId}`;
    setSubmitError(null);
    setContextStatus((status) => (status === "ready" ? "ready" : "loading"));
    (documentAnchored
      ? getWorkItemContext(documentId)
      : getMatterWorkItemContext(matterId)
    )
      .then((response) => {
        if (cancelled) return;
        const ctx = response?.data || {};
        setContext(ctx);
        setContextStatus("ready");
        setContextFor(loadingKey);
        const ids = (ctx.recipients || []).map((r) => r.id);
        setAssigneeId((current) =>
          current != null && ids.includes(current)
            ? current
            : ctx.default_recipient_id || null
        );
        const pinIds = new Set((ctx.conditions || []).map((c) => c.id));
        setConditionIds((current) => current.filter((id) => pinIds.has(id)));
        setQueued((current) =>
          current.map((item) => ({
            ...item,
            conditionIds: item.conditionIds.filter((id) => pinIds.has(id)),
          }))
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setContextStatus("failed");
        setSubmitError(
          err?.response?.data?.detail ||
            "Couldn't reach the new Salesforce to load the matter's team."
        );
      });
    return () => {
      cancelled = true;
    };
  }, [open, documentId, matterId, documentAnchored]);

  const recipientOptions = useMemo(
    () =>
      (context?.recipients || []).map((r) => ({
        value: r.id,
        label: r.role ? `${r.name} — ${r.role}` : r.name,
      })),
    [context]
  );

  // The doorway's own composer list wins when the context carries one
  // (org order, so new types like "Prepare Brief" show up without a
  // frontend release) — the hardcoded list is only for an older
  // doorway that answers without `types`.
  const typeOptions = useMemo(() => {
    const fromContext = context?.types;
    if (Array.isArray(fromContext) && fromContext.length > 0) {
      return fromContext.map((t) => ({ value: t.value, label: t.label }));
    }
    return WORK_ITEM_TYPES.map((t) => ({ value: t, label: t }));
  }, [context]);

  const labelFor = useCallback(
    (value) => typeOptions.find((t) => t.value === value)?.label || value,
    [typeOptions]
  );

  // The matter's conditions for the pin picker. An older doorway (or a
  // matter with no conditions yet) sends none — then there is nothing
  // to pin and the picker stays out of the way.
  const conditionOptions = useMemo(
    () =>
      (context?.conditions || []).map((c) => ({
        value: c.id,
        label: c.detail ? `${c.name} · ${c.detail}` : c.name,
        name: c.name,
      })),
    [context]
  );
  const namesFor = useCallback(
    (ids) =>
      ids
        .map((id) => conditionOptions.find((c) => c.value === id)?.name)
        .filter(Boolean),
    [conditionOptions]
  );
  const pinnedNames = useMemo(
    () => namesFor(conditionIds),
    [namesFor, conditionIds]
  );

  /* ---- what state the composer is in ----------------------------- */

  const trimmedInstruction = instruction.trim();
  // "Other" is the one type that cannot title itself — left blank the
  // item would land in their list called simply "Other". Inside a plan
  // every step composes its own subject, so only a lone Other asks.
  const needsCustomSubject = type === "Other" && steps.length === 0;
  const stepHasType = type != null;
  const stepHasWords = trimmedInstruction.length > 0;
  const stepEmpty = !stepHasType && !stepHasWords;
  const stepFilled =
    stepHasType &&
    stepHasWords &&
    (!needsCustomSubject || hasText(subject));
  // Started but not finished: a type with no words, words with no type,
  // or a lone Other with no subject. Nothing moves until it is resolved.
  const stepHalf = !stepEmpty && !stepFilled;

  const stepCount = steps.length + (stepFilled ? 1 : 0);
  const itemReady = stepCount >= 1 && !stepHalf;
  const totalItems = queued.length + (itemReady ? 1 : 0);

  const currentItem = useCallback(
    () => ({
      steps: [
        ...steps,
        ...(stepFilled ? [{ type, instruction: trimmedInstruction }] : []),
      ],
      subject:
        steps.length === 0 && type === "Other" && stepFilled
          ? subject.trim()
          : "",
      conditionIds,
      dueDate,
    }),
    [steps, stepFilled, type, trimmedInstruction, subject, conditionIds, dueDate]
  );

  const focusInstruction = useCallback(() => {
    window.setTimeout(() => {
      const node = textAreaRef.current?.resizableTextArea?.textArea;
      if (node) node.focus();
    }, 0);
  }, []);

  const handlePickType = useCallback(
    (value) => {
      setType((current) => (current === value ? null : value));
      focusInstruction();
    },
    [focusInstruction]
  );

  // "Add a step": the step so far joins the plan; the box is ready for
  // the next one. Only a finished step can be added.
  const handleAddStep = useCallback(() => {
    if (!stepFilled) return;
    setSteps((current) => [
      ...current,
      { type, instruction: trimmedInstruction },
    ]);
    setType(null);
    setSubject("");
    setInstruction("");
    focusInstruction();
  }, [stepFilled, type, trimmedInstruction, focusInstruction]);

  const handleRemoveStep = useCallback((index) => {
    setSteps((current) => current.filter((_, i) => i !== index));
  }, []);

  // "Add work item": this one (single or plan) goes on the list; the
  // composer starts fresh for the next.
  const handleAddWorkItem = useCallback(() => {
    if (!itemReady) return;
    const item = { id: nextQueuedId++, ...currentItem() };
    setQueued((current) => [...current, item]);
    resetComposer();
    setSubmitError(null);
    focusInstruction();
  }, [itemReady, currentItem, resetComposer, focusInstruction]);

  const handleRemoveQueued = useCallback((id) => {
    setQueued((current) => current.filter((item) => item.id !== id));
  }, []);

  const formReady =
    contextStatus === "ready" && context?.enabled && context?.matter_found;

  const canSubmit =
    formReady &&
    contextIsCurrent &&
    assigneeId != null &&
    totalItems >= 1 &&
    !stepHalf &&
    !submitting;

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    const items = [...queued, ...(itemReady ? [currentItem()] : [])];
    if (items.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    const body = { assignee_id: assigneeId };
    if (items.length === 1) {
      Object.assign(body, workItemBody(items[0]));
    } else {
      body.items = items.map(workItemBody);
    }
    const planSteps = items.length === 1 ? items[0].steps.length : 0;
    (documentAnchored
      ? createWorkItem(documentId, body)
      : createMatterWorkItem(matterId, body)
    )
      .then((response) => {
        const number = response?.data?.work_item_number;
        if (items.length > 1) {
          message.success(
            `${items.length} work items sent — all in their court now.`
          );
        } else if (planSteps > 1) {
          message.success(
            `Plan sent — ${planSteps} steps. The first is in their court ` +
              "now; the rest wait their turn."
          );
        } else {
          message.success(
            number
              ? `Work item ${number} sent — it's in their court now.`
              : "Work item sent — it's in their court now."
          );
        }
        if (onCreated) onCreated();
        // Stay open so they can send the next one from the next letter.
        resetComposer();
        setQueued([]);
        setSubmitError(null);
      })
      .catch((err) => {
        setSubmitError(
          err?.response?.data?.detail ||
            "Couldn't create the work item. Please try again."
        );
      })
      .finally(() => setSubmitting(false));
  }, [
    canSubmit,
    queued,
    itemReady,
    currentItem,
    assigneeId,
    documentAnchored,
    documentId,
    matterId,
    onCreated,
    resetComposer,
  ]);

  const title = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <ArrowsLeftRight size={16} weight="bold" />
      Create Work Item
    </span>
  );

  // The three non-form states render as explanations, not forms.
  const blockedExplanation = useMemo(() => {
    if (contextStatus === "loading") return null;
    if (contextStatus === "failed") return submitError;
    if (contextStatus === "ready" && context && !context.enabled) {
      return "Work items aren't switched on in this environment yet.";
    }
    if (contextStatus === "ready" && context && !context.matter_found) {
      return (
        "This matter hasn't migrated to the new Salesforce yet, so it has " +
        "no Work Items loop. Use Create Task — it still reaches Litify."
      );
    }
    return null;
  }, [contextStatus, context, submitError]);

  /* ---- words ------------------------------------------------------- */

  const describeItem = useCallback(
    (item) => {
      const pins = conditionLabel(namesFor(item.conditionIds));
      const tail =
        (pins ? ` — ${pins}` : "") +
        (item.dueDate ? ` · due ${item.dueDate.format("ddd D MMM")}` : "");
      if (item.steps.length === 1) {
        const head = item.subject || labelFor(item.steps[0].type);
        return `${head}${tail}`;
      }
      return (
        `Plan · ${item.steps.length} steps: ` +
        item.steps.map((s) => labelFor(s.type)).join(" → ") +
        tail
      );
    },
    [namesFor, labelFor]
  );

  const subjectPreview = useMemo(() => {
    if (!stepHasType || needsCustomSubject) return null;
    const pins = conditionLabel(pinnedNames);
    if (steps.length > 0) {
      return pins
        ? `Each step will read “<type> — ${pins}”.`
        : "Each step will read as its type, on the whole matter.";
    }
    return pins
      ? `Subject: ${labelFor(type)} — ${pins}`
      : `Subject: ${labelFor(type)} (whole matter)`;
  }, [stepHasType, needsCustomSubject, pinnedNames, steps.length, labelFor, type]);

  const halfWarning = !stepHalf
    ? null
    : !stepHasType
    ? "Press a chip above to say what kind of work this is."
    : !stepHasWords
    ? `Write the instruction for ${steps.length ? "this step" : "this work item"}.`
    : "Give it a subject — “Other” has no label of its own.";

  const stepNumber = steps.length + 1;
  const composerTitle = `Work item ${queued.length + 1}${
    steps.length ? ` · step ${stepNumber}` : ""
  }`;

  let okText = "Send";
  if (totalItems > 1) okText = `Send ${totalItems} work items`;
  else if (totalItems === 1 && queued.length === 0 && stepCount > 1) {
    okText = `Send plan · ${stepCount} steps`;
  }

  let footerHint;
  if (totalItems > 1) {
    footerHint =
      `They get one bell in the new Salesforce and all ${totalItems} land ` +
      "in their Work Items at once, each returned on its own.";
  } else if (stepCount > 1 && queued.length === 0) {
    footerHint =
      "They get one bell in the new Salesforce and every step lands in " +
      "their Work Items. Only step 1 is live today; the rest sit greyed as " +
      "“Coming up” until you close the one before.";
  } else {
    footerHint =
      "They get a bell in the new Salesforce and this lands in their Work " +
      "Items. When they return it, it comes back to you.";
  }

  return (
    <BesideDocumentDrawer
      open={open}
      onClose={onClose}
      onOk={handleSubmit}
      okText={okText}
      okDisabled={!canSubmit}
      okLoading={submitting}
      hideOk={!formReady}
      cancelText={formReady ? "Cancel" : "Close"}
      title={title}
    >
      {contextStatus === "loading" && (
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          <Spin />
          <InboxModalHint style={{ marginTop: 8 }}>
            Checking the new Salesforce for this matter…
          </InboxModalHint>
        </div>
      )}

      {blockedExplanation && (
        <InboxModalErrorBanner role="alert">
          {blockedExplanation}
        </InboxModalErrorBanner>
      )}

      {formReady && (
        <>
          <InboxModalHint style={{ marginBottom: 12 }}>
            {(context.client_name || context.matter_name
              ? `Goes on ${context.client_name || context.matter_name}'s ` +
                "matter in the new Salesforce"
              : "Goes on this matter in the new Salesforce") +
              (documentAnchored
                ? ", with a link back to the letter you're looking at."
                : ".")}
          </InboxModalHint>

          <InboxModalFieldGroup>
            <InboxModalLabel htmlFor="work-item-assignee">
              Send to
            </InboxModalLabel>
            <Select
              id="work-item-assignee"
              style={{ width: "100%" }}
              placeholder="Choose who this goes to…"
              options={recipientOptions}
              value={assigneeId}
              onChange={setAssigneeId}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </InboxModalFieldGroup>

          {queued.length > 0 && (
            <InboxModalFieldGroup>
              <InboxModalLabel as="div" id="work-item-ready-label">
                Ready to send ({queued.length})
              </InboxModalLabel>
              <ul
                style={readyListStyle}
                aria-labelledby="work-item-ready-label"
                data-testid="work-item-ready-list"
              >
                {queued.map((item, index) => (
                  <li
                    key={item.id}
                    style={{
                      ...readyRowStyle,
                      borderTop: index === 0 ? "none" : readyRowStyle.borderTop,
                    }}
                  >
                    <span>
                      <strong>{index + 1}.</strong> {describeItem(item)}
                    </span>
                    <button
                      type="button"
                      style={tinyXStyle}
                      onClick={() => handleRemoveQueued(item.id)}
                      aria-label={`Remove work item ${index + 1}`}
                      title="Remove from this send"
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
              <InboxModalHint>
                These go out together when you press Send, each on its own.
              </InboxModalHint>
            </InboxModalFieldGroup>
          )}

          <div style={composerStyle} data-testid="work-item-composer">
            <div style={composerHeadStyle}>
              <span>{composerTitle}</span>
              {steps.length > 0 && (
                <span style={{ fontWeight: 400, color: "#722ed1" }}>
                  a plan, in order
                </span>
              )}
            </div>

            {steps.length > 0 && (
              <div style={planStripStyle} data-testid="work-item-plan-steps">
                <div style={{ fontWeight: 600, marginBottom: 2 }}>
                  Plan so far
                </div>
                {steps.map((step, index) => (
                  <React.Fragment key={`${index}-${step.type}`}>
                    {index > 0 && <span style={{ margin: "0 2px" }}>→</span>}
                    <span style={stepPillStyle}>
                      {index + 1}. {labelFor(step.type)}
                      <button
                        type="button"
                        style={tinyXStyle}
                        onClick={() => handleRemoveStep(index)}
                        aria-label={`Remove step ${index + 1}`}
                        title="Remove this step"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  </React.Fragment>
                ))}
                <div style={{ color: "#595959", marginTop: 2 }}>
                  Step {stepNumber} opens when step {steps.length} is closed.
                </div>
              </div>
            )}

            <InboxModalFieldGroup>
              <InboxModalLabel as="div" id="work-item-instruct-label">
                Instruct
              </InboxModalLabel>
              <div
                style={chipRowStyle}
                role="group"
                aria-labelledby="work-item-instruct-label"
              >
                {typeOptions.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    style={type === t.value ? chipOnStyle : chipStyle}
                    aria-pressed={type === t.value}
                    onClick={() => handlePickType(t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <InboxModalHint>
                {steps.length > 0
                  ? `Pick what kind of work step ${stepNumber} is.`
                  : "Pick what kind of work this is."}
              </InboxModalHint>
            </InboxModalFieldGroup>

            {needsCustomSubject && (
              <InboxModalFieldGroup>
                <InboxModalLabel htmlFor="work-item-subject">
                  Subject
                </InboxModalLabel>
                <Input
                  id="work-item-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g. Check the C-file receipt"
                  maxLength={255}
                />
              </InboxModalFieldGroup>
            )}

            <InboxModalFieldGroup>
              <InboxModalLabel htmlFor="work-item-instruction">
                {steps.length > 0 ? `Step ${stepNumber} instruction` : "Instruction"}
              </InboxModalLabel>
              <Input.TextArea
                id="work-item-instruction"
                ref={textAreaRef}
                autoSize={{ minRows: 4, maxRows: 10 }}
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                placeholder={
                  steps.length > 0
                    ? "What should happen in this step?"
                    : "e.g. VA says we submitted two docs — pull the C-file " +
                      "receipt and confirm both went in."
                }
                maxLength={8000}
              />
              {subjectPreview && (
                <InboxModalHint data-testid="work-item-subject-preview">
                  {subjectPreview}
                </InboxModalHint>
              )}
            </InboxModalFieldGroup>

            {conditionOptions.length > 0 && (
              <InboxModalFieldGroup>
                <InboxModalLabel htmlFor="work-item-conditions">
                  Conditions
                </InboxModalLabel>
                <Select
                  id="work-item-conditions"
                  mode="multiple"
                  style={{ width: "100%" }}
                  placeholder="Whole matter — no particular condition"
                  options={conditionOptions}
                  value={conditionIds}
                  onChange={setConditionIds}
                  maxTagCount="responsive"
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
                <InboxModalHint>
                  Pick one, several, or none. One instruction covering seven
                  conditions is one work item, returned once.
                </InboxModalHint>
              </InboxModalFieldGroup>
            )}

            <InboxModalFieldGroup>
              <InboxModalLabel htmlFor="work-item-due-date">
                Due
              </InboxModalLabel>
              <DatePicker
                id="work-item-due-date"
                style={{ width: "100%" }}
                value={dueDate}
                onChange={setDueDate}
                format="ddd D MMM YYYY"
                placeholder="No date — nothing will chase it"
                // Nothing is due before it is asked for.
                disabledDate={(candidate) => candidate.isBefore(dayjs(), "day")}
              />
              <InboxModalHint>
                A date written into the instruction is only words. This is
                what turns the item red when it runs late and puts it on
                their Due Soon list.
                {steps.length > 0 ? " Every step of the plan takes it." : ""}
              </InboxModalHint>
            </InboxModalFieldGroup>

            {halfWarning && (
              <div style={warnStyle} data-testid="work-item-half-warning">
                {halfWarning}
              </div>
            )}

            <div style={actionsRowStyle}>
              <div>
                <Button
                  block
                  icon={<Plus size={12} weight="bold" />}
                  disabled={!stepFilled}
                  onClick={handleAddStep}
                >
                  Add a step
                </Button>
                <p style={actionHintStyle}>
                  {steps.length > 0
                    ? `Adds step ${stepNumber}; it opens when step ${steps.length} is closed.`
                    : "Makes this a plan: the next step opens when this one is closed."}
                </p>
              </div>
              <div>
                <Button
                  block
                  icon={<Plus size={12} weight="bold" />}
                  disabled={!itemReady}
                  onClick={handleAddWorkItem}
                >
                  Add work item
                </Button>
                <p style={actionHintStyle}>
                  Puts this on the list to send and starts a fresh one. Just
                  the one? Press Send.
                </p>
              </div>
            </div>
          </div>

          <InboxModalHint style={{ marginTop: 10 }}>{footerHint}</InboxModalHint>

          {submitError && (
            <InboxModalErrorBanner role="alert">
              {submitError}
            </InboxModalErrorBanner>
          )}
        </>
      )}
    </BesideDocumentDrawer>
  );
};

CreateWorkItemModal.propTypes = {
  open: PropTypes.bool.isRequired,
  /** Inbox anchor: compose from this document (deep link appended). */
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Matter anchor: compose from the matter page, no document. */
  matterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  /** Called after a successful create, before onClose — refresh lists. */
  onCreated: PropTypes.func,
};

export default CreateWorkItemModal;
